/**
 * InfinitSales - Card "Trocar senha" da Minha Conta (2026-10-14)
 *
 * Chama POST /api/users/me/password (senha atual + nova). Funciona com ou sem
 * o e-mail transacional ligado; quando ligado, o backend avisa o usuário por
 * e-mail que a senha mudou. Senha atual errada volta 400 (nunca 401, que
 * derrubaria a sessão no interceptor do axios).
 */
import React, { useState } from 'react';
import { Lock, Eye, EyeOff, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import authService from '../../services/global/authService';

const errorText = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (detail && typeof detail.detail === 'string') return detail.detail;
  return fallback;
};

const ChangePasswordCard = ({ colors, cardStyle, titleStyle }) => {
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const update = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (form.next.length < 6) {
      setError('A senha nova deve ter pelo menos 6 caracteres.');
      return;
    }
    if (form.next !== form.confirm) {
      setError('A confirmação não bate com a senha nova.');
      return;
    }
    setSaving(true);
    try {
      await authService.changePassword(form.current, form.next);
      setForm({ current: '', next: '', confirm: '' });
      setSuccess('Senha alterada. Use a senha nova no próximo login.');
    } catch (err) {
      setError(errorText(err, 'Não foi possível trocar a senha agora. Tente de novo.'));
    } finally {
      setSaving(false);
    }
  };

  const input = {
    width: '100%',
    padding: '10px 12px',
    border: `1px solid ${colors.borderColor}`,
    borderRadius: '10px',
    backgroundColor: colors.bgSecondary,
    color: colors.textPrimary,
    fontSize: '14px',
    outline: 'none',
  };
  const label = {
    display: 'block',
    fontSize: '12px',
    fontWeight: 500,
    color: colors.textSecondary,
    marginBottom: '4px',
  };
  const type = show ? 'text' : 'password';

  return (
    <form style={cardStyle} onSubmit={handleSubmit}>
      <div style={{ ...titleStyle, justifyContent: 'space-between' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Lock size={14} color={colors.textSecondary} />
          Trocar senha
        </span>
        <button
          type="button"
          onClick={() => setShow(!show)}
          title={show ? 'Esconder senhas' : 'Mostrar senhas'}
          style={{ background: 'transparent', border: 'none', color: colors.textSecondary, cursor: 'pointer', display: 'flex', padding: 4 }}
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
        <div>
          <label style={label} htmlFor="current-password">Senha atual</label>
          <input
            id="current-password"
            style={input}
            type={type}
            value={form.current}
            onChange={update('current')}
            autoComplete="current-password"
            required
          />
        </div>
        <div>
          <label style={label} htmlFor="new-password">Senha nova</label>
          <input
            id="new-password"
            style={input}
            type={type}
            value={form.next}
            onChange={update('next')}
            placeholder="mín. 6 caracteres"
            autoComplete="new-password"
            minLength={6}
            required
          />
        </div>
        <div>
          <label style={label} htmlFor="confirm-password">Confirmar senha nova</label>
          <input
            id="confirm-password"
            style={input}
            type={type}
            value={form.confirm}
            onChange={update('confirm')}
            autoComplete="new-password"
            minLength={6}
            required
          />
        </div>
      </div>

      {(error || success) && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px',
          padding: '10px 14px', borderRadius: '10px', fontSize: '13px',
          backgroundColor: error ? '#ef444415' : `${colors.accentPrimary}15`,
          color: error ? '#ef4444' : colors.accentPrimary,
        }}>
          {error ? <AlertCircle size={14} /> : <CheckCircle2 size={14} />}
          {error || success}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
        <button
          type="submit"
          disabled={saving}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '10px 16px', border: 'none', borderRadius: '10px',
            backgroundColor: colors.accentPrimary, color: 'white',
            fontSize: '14px', fontWeight: 600,
            cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1,
          }}
        >
          {saving && <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />}
          Salvar senha nova
        </button>
      </div>
    </form>
  );
};

export default ChangePasswordCard;
